import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useAppConfig } from '@/contexts/AppConfigContext.jsx';
import { useTheme } from '@/contexts/ThemeContext';
import { useCategoryEnvelope } from '@/context/CategoryEnvelopeContext';
import DashboardHeader from '@/components/DashboardHeader';
import BottomNavigation from '@/components/BottomNavigation';
import {
  getAllDeletedItemsAcrossLists,
  restoreDeletedItem
} from '@/services/shoppingListService';
import {
  User,
  Globe,
  Moon,
  Sun,
  Download,
  Upload,
  Trash2,
  Shield,
  Bell,
  Info,
  Mail,
  FileText,
  Lock,
  ChevronRight,
  LogOut,
  Check,
  AlertCircle,
  HelpCircle,
  BookOpen,
  RotateCcw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { doc, updateDoc, getDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { signOut } from 'firebase/auth';
import { auth } from '@/config/firebase';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import { X, AlertTriangle } from 'lucide-react';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, isAdmin, refreshUser } = useAuth();
  const {
    billingLocale,
    availableBillingLocales,
    billingConfig,
    updateBillingLocale,
    updatingBillingLocale,
  } = useAppConfig();
  const { theme, toggleTheme } = useTheme();
  const { clearCache: clearCategoryEnvelopeCache } = useCategoryEnvelope();
  const [loading, setLoading] = useState(false);
  // Clear data modal state
  const [showClearDataModal, setShowClearDataModal] = useState(false);
  const [clearStep, setClearStep] = useState(1); // 1 = initial warning, 2 = final confirm
  const [deletePhrase, setDeletePhrase] = useState('');
  const REQUIRED_PHRASE = 'DELETE ALL';

  // Profile settings
  const [displayName, setDisplayName] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [comparisonCurrency, setComparisonCurrency] = useState(null); // FX comparison currency
  const [editingName, setEditingName] = useState(false);

  // Privacy settings
  const [localOnlyMode, setLocalOnlyMode] = useState(false);

  // Notification settings
  const [budgetAlerts, setBudgetAlerts] = useState(true);
  const [goalReminders, setGoalReminders] = useState(true);
  const [selectedBillingLocale, setSelectedBillingLocale] = useState(billingLocale);

  // Trash section state
  const [showTrashSection, setShowTrashSection] = useState(false);
  const [allDeletedItems, setAllDeletedItems] = useState([]);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [restoringItemId, setRestoringItemId] = useState(null);

  // Function to load all deleted items
  const loadAllDeletedItems = async () => {
    if (!user?.uid) return;
    setLoadingTrash(true);
    try {
      const items = await getAllDeletedItemsAcrossLists(user.uid, { limit: 100 });
      setAllDeletedItems(items);
    } catch (error) {
      console.error('Error loading deleted items:', error);
      toast.error('Failed to load deleted items');
    } finally {
      setLoadingTrash(false);
    }
  };

  // Load deleted items when trash section is expanded
  useEffect(() => {
    if (showTrashSection && user?.uid) {
      loadAllDeletedItems();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTrashSection, user?.uid]);

  // Handle restoring an item
  const handleRestoreItem = async (item) => {
    if (!user?.uid || !item.listId) return;
    setRestoringItemId(item.id);
    try {
      await restoreDeletedItem(user.uid, item.listId, item.id);
      toast.success(`"${item.name}" restored to ${item.listName}`);
      // Refresh the list
      await loadAllDeletedItems();
    } catch (error) {
      console.error('Error restoring item:', error);
      toast.error('Failed to restore item');
    } finally {
      setRestoringItemId(null);
    }
  };

  useEffect(() => {
    setSelectedBillingLocale(billingLocale);
  }, [billingLocale]);

  const CURRENCIES = [
    { code: 'USD', name: 'US Dollar', symbol: '$', region: 'United States' },
    { code: 'EUR', name: 'Euro', symbol: '€', region: 'Eurozone' },
    { code: 'GBP', name: 'British Pound Sterling', symbol: '£', region: 'United Kingdom' },
    { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', region: 'Nigeria' },
    { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', region: 'Kenya' },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R', region: 'South Africa' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', region: 'Canada' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', region: 'Australia' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹', region: 'India' },
    { code: 'CNY', name: 'Chinese Yuan Renminbi', symbol: '¥', region: 'China' },
    // Bonus currencies
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥', region: 'Japan' },
    { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵', region: 'Ghana' },
    { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh', region: 'Tanzania' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', region: 'UAE' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'Fr', region: 'Switzerland' },
  ];

  // Load user preferences
  useEffect(() => {
    if (!user?.uid) return;

    const loadPreferences = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setDisplayName(data.displayName || user.displayName || '');
          setCurrency(data.currency || 'GBP');
          setComparisonCurrency(data.comparisonCurrency || null);
          setLocalOnlyMode(data.localOnlyMode || false);
          setBudgetAlerts(data.budgetAlerts !== false);
          setGoalReminders(data.goalReminders !== false);
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    };

    loadPreferences();
  }, [user]);

  // Update display name
  const handleUpdateName = async () => {
    if (!displayName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        displayName: displayName.trim(),
      });
      toast.success('Name updated successfully');
      setEditingName(false);
    } catch (error) {
      console.error('Error updating name:', error);
      toast.error('Failed to update name');
    } finally {
      setLoading(false);
    }
  };

  // Update currency
  const handleUpdateCurrency = async (newCurrency) => {
    setLoading(true);
    try {
      console.log('⚙️ Updating currency to:', newCurrency);
      await updateDoc(doc(db, 'users', user.uid), {
        currency: newCurrency,
      });
      
      // Verify the update
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const savedCurrency = userDoc.data()?.currency;
      console.log('⚙️ Currency saved in Firestore:', savedCurrency);
      
      setCurrency(newCurrency);
      console.log('⚙️ Refreshing user profile...');
      // Refresh the auth context to update currency globally
      await refreshUser();
      console.log('⚙️ User profile refreshed successfully');
      toast.success(`Currency updated to ${newCurrency}`);
    } catch (error) {
      console.error('Error updating currency:', error);
      toast.error('Failed to update currency');
    } finally {
      setLoading(false);
    }
  };

  // Update comparison currency (for FX display)
  const handleUpdateComparisonCurrency = async (newComparisonCurrency) => {
    setLoading(true);
    try {
      // If selecting the same as base currency or "None", set to null
      const valueToSave = (newComparisonCurrency === currency || newComparisonCurrency === 'none') 
        ? null 
        : newComparisonCurrency;
      
      console.log('💱 Updating comparison currency to:', valueToSave);
      await updateDoc(doc(db, 'users', user.uid), {
        comparisonCurrency: valueToSave,
      });
      
      setComparisonCurrency(valueToSave);
      await refreshUser();
      
      if (valueToSave) {
        toast.success(`Comparison currency set to ${valueToSave}`);
      } else {
        toast.success('Comparison currency disabled');
      }
    } catch (error) {
      console.error('Error updating comparison currency:', error);
      toast.error('Failed to update comparison currency');
    } finally {
      setLoading(false);
    }
  };

  // Toggle notification settings
  const handleToggleNotification = async (type, value) => {
    setLoading(true);
    try {
      const updates = type === 'budget' 
        ? { budgetAlerts: value }
        : { goalReminders: value };

      await updateDoc(doc(db, 'users', user.uid), updates);
      
      if (type === 'budget') {
        setBudgetAlerts(value);
      } else {
        setGoalReminders(value);
      }
      
      toast.success('Notification preference updated');
    } catch (error) {
      console.error('Error updating notification:', error);
      toast.error('Failed to update notification preference');
    } finally {
      setLoading(false);
    }
  };

  // Toggle local-only mode
  const handleToggleLocalMode = async (value) => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        localOnlyMode: value,
      });
      setLocalOnlyMode(value);
      toast.success(value ? 'Local-only mode enabled' : 'Local-only mode disabled');
    } catch (error) {
      console.error('Error updating local mode:', error);
      toast.error('Failed to update privacy setting');
    } finally {
      setLoading(false);
    }
  };

  // Export data to CSV
  const handleExportCSV = async () => {
    setLoading(true);
    try {
      // Fetch all user data
      const [transactions, goals, categories] = await Promise.all([
        getDocs(collection(db, 'users', user.uid, 'transactions')),
        getDocs(collection(db, 'users', user.uid, 'goals')),
        getDocs(collection(db, 'users', user.uid, 'categories')),
      ]);

      // Convert transactions to CSV
      const transactionsData = transactions.docs.map(doc => {
        const data = doc.data();
        return {
          Date: data.date?.toDate().toISOString(),
          Type: data.type,
          Category: data.category,
          Amount: data.amount,
          Description: data.description || '',
          Notes: data.notes || '',
        };
      });

      // Create CSV content
      const headers = Object.keys(transactionsData[0] || {}).join(',');
      const rows = transactionsData.map(row => 
        Object.values(row).map(val => `"${val}"`).join(',')
      ).join('\n');
      const csv = `${headers}\n${rows}`;

      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `finlist-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  // Export data to JSON (Excel compatible)
  const handleExportJSON = async () => {
    setLoading(true);
    try {
      // Fetch all user data
      const [transactions, goals, shoppingLists] = await Promise.all([
        getDocs(collection(db, 'users', user.uid, 'transactions')),
        getDocs(collection(db, 'users', user.uid, 'goals')),
        getDocs(collection(db, 'users', user.uid, 'shoppingLists')),
      ]);

      const exportData = {
        exportDate: new Date().toISOString(),
        user: {
          email: user.email,
          displayName: displayName,
        },
        transactions: transactions.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate().toISOString(),
        })),
        goals: goals.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          dueDate: doc.data().dueDate?.toDate().toISOString(),
        })),
        shoppingLists: shoppingLists.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })),
      };

      // Download JSON
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `finlist-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Backup created successfully');
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBillingLocale = async () => {
    if (selectedBillingLocale === billingLocale) return;
    try {
      await updateBillingLocale(selectedBillingLocale);
    } catch (error) {
      console.error('Error updating billing locale:', error);
    }
  };

  // Perform the destructive clear
  const performClearAllData = async () => {
    setLoading(true);
    try {
      const collections = [
        'transactions',
        'goals',
        'savingsGoals',
        'shoppingLists',
        'budgets',
        'categories',
        'settings',
      ];
      for (const collectionName of collections) {
        const snapshot = await getDocs(collection(db, 'users', user.uid, collectionName));
        await Promise.all(snapshot.docs.map(d => deleteDoc(d.ref)));
      }
      // Delete todoMeta docs
      const todoMetaSnapshot = await getDocs(collection(db, 'users', user.uid, 'todoMeta'));
      await Promise.all(todoMetaSnapshot.docs.map(d => deleteDoc(d.ref)));
      // Delete todoLists and nested tasks
      const todoListsSnapshot = await getDocs(collection(db, 'users', user.uid, 'todoLists'));
      for (const listDoc of todoListsSnapshot.docs) {
        const tasksSnapshot = await getDocs(collection(db, 'users', user.uid, 'todoLists', listDoc.id, 'tasks'));
        await Promise.all(tasksSnapshot.docs.map(t => deleteDoc(t.ref)));
        await deleteDoc(listDoc.ref);
      }
      
      // CRITICAL: Clear the CategoryEnvelope cache to prevent stale data
      // This ensures the UI reflects the empty state immediately
      clearCategoryEnvelopeCache();
      
      toast.success('All data cleared successfully');
      setShowClearDataModal(false);
      setDeletePhrase('');
      setClearStep(1);
    } catch (error) {
      console.error('Error clearing data:', error);
      toast.error('Failed to clear data');
    } finally {
      setLoading(false);
    }
  };

  // Logout - RULE 1: Manual sign-out redirects to /welcome
  const handleLogout = async () => {
    try {
      // Clear route memory before signing out
      try {
        sessionStorage.removeItem('finlist_last_route');
        localStorage.removeItem('finlist_last_route');
        sessionStorage.removeItem('finlist_auth_return_path');
        localStorage.removeItem('finlist_auth_return_path');
      } catch (e) {
        console.warn('Could not clear route storage:', e);
      }
      
      await signOut(auth);
      toast.success('Logged out successfully');
      
      // RULE 1: Always redirect to welcome page after manual sign-out
      navigate('/welcome', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to log out');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 pb-20 transition-colors">
      <div className="px-3 sm:px-4 md:px-6 py-4 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Settings & Privacy
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Manage your account and preferences</p>
        </div>

        <div className="space-y-4">
          {isAdmin && (
            <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] dark:shadow-slate-900/50 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  Admin Controls
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Configure billing locale for all marketing copy.</p>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Active billing locale</label>
                  <div className="grid gap-3">
                    <select
                      value={selectedBillingLocale}
                      onChange={(e) => setSelectedBillingLocale(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      {availableBillingLocales.map((locale) => (
                        <option key={locale.locale} value={locale.locale}>
                          {locale.regionLabel} · {locale.priceDisplay} ({locale.currencyCode})
                        </option>
                      ))}
                    </select>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Last updated {billingConfig.updatedAt ? new Date(billingConfig.updatedAt).toLocaleString() : 'never'}
                      {billingConfig.updatedBy?.email && ` by ${billingConfig.updatedBy.email}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <Button
                    variant="outline"
                    disabled={selectedBillingLocale === billingLocale || updatingBillingLocale}
                    loading={updatingBillingLocale}
                    onClick={() => setSelectedBillingLocale(billingLocale)}
                  >
                    Reset
                  </Button>
                  <Button
                    disabled={selectedBillingLocale === billingLocale || updatingBillingLocale}
                    loading={updatingBillingLocale}
                    onClick={handleUpdateBillingLocale}
                  >
                    Update Locale
                  </Button>
                </div>
              </div>
            </section>
          )}
          {/* Profile Section */}
          <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] dark:shadow-slate-900/20 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <User className="w-5 h-5 text-teal-600" />
                Profile
              </h2>
            </div>
            <div className="p-5 space-y-4">
              {/* Display Name */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Display Name
                </label>
                {editingName ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Your name"
                    />
                    <button
                      onClick={handleUpdateName}
                      disabled={loading}
                      className="px-4 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setEditingName(false)}
                      className="px-4 py-2 bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => setEditingName(true)}
                    className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-slate-700 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                  >
                    <span className="text-gray-900 dark:text-white">{displayName || 'Not set'}</span>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Email
                </label>
                <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-slate-700 rounded-xl">
                  <Mail className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-gray-900 dark:text-white">{user?.email}</span>
                </div>
              </div>

              {/* Currency */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Currency
                </label>
                <div className="relative">
                  <select
                    value={currency}
                    onChange={(e) => handleUpdateCurrency(e.target.value)}
                    disabled={loading}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl appearance-none cursor-pointer dark:hover:bg-slate-600 hover:bg-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors text-gray-900 dark:text-white font-medium"
                  >
                    {CURRENCIES.map((curr) => (
                      <option key={curr.code} value={curr.code}>
                        {curr.symbol} {curr.name} ({curr.code})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Selected: {CURRENCIES.find(c => c.code === currency)?.name || currency}
                </p>
              </div>

              {/* Comparison Currency (FX Display) */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Comparison Currency
                  <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                    (Show amounts in a secondary currency)
                  </span>
                </label>
                <div className="relative">
                  <select
                    value={comparisonCurrency || 'none'}
                    onChange={(e) => handleUpdateComparisonCurrency(e.target.value)}
                    disabled={loading}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl appearance-none cursor-pointer dark:hover:bg-slate-600 hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-gray-900 dark:text-white font-medium"
                  >
                    <option value="none">🚫 None - Disable comparison</option>
                    {CURRENCIES.filter(c => c.code !== currency).map((curr) => (
                      <option key={curr.code} value={curr.code}>
                        {curr.symbol} {curr.name} ({curr.code})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {comparisonCurrency 
                    ? `Showing amounts in ${comparisonCurrency} alongside ${currency}`
                    : 'No comparison currency set'}
                </p>
              </div>

              {/* Theme */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Theme
                </label>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleTheme();
                  }}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-slate-700 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {theme === 'dark' ? (
                      <Moon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                    ) : (
                      <Sun className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                    )}
                    <span className="text-gray-900 dark:text-white">
                      {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                    </span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>
          </section>

          {/* Data Management Section */}
          <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] dark:shadow-slate-900/20 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                Data Management
              </h2>
            </div>
            <div className="p-5 space-y-3">
              <button
                onClick={handleExportCSV}
                disabled={loading}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-slate-700 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <Download className="w-5 h-5 text-green-600" />
                  <div className="text-left">
                    <p className="text-gray-900 dark:text-white font-medium">Export to CSV</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Download transactions as CSV file</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>

              <button
                onClick={handleExportJSON}
                disabled={loading}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-slate-700 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <Download className="w-5 h-5 text-blue-600" />
                  <div className="text-left">
                    <p className="text-gray-900 dark:text-white font-medium">Export Full Backup</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Download all data (Excel compatible)</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>

              <button
                onClick={() => document.getElementById('import-file').click()}
                disabled={loading}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-slate-700 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <Upload className="w-5 h-5 text-purple-600" />
                  <div className="text-left">
                    <p className="text-gray-900 dark:text-white font-medium">Import Backup</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Restore data from backup file</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
              <input
                id="import-file"
                type="file"
                accept=".json"
                className="hidden"
                onChange={(e) => {
                  // TODO: Implement import functionality
                  toast.info('Import feature coming soon');
                }}
              />
            </div>
          </section>

          {/* Privacy Section */}
          <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] dark:shadow-slate-900/20 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Privacy & Security
              </h2>
            </div>
            <div className="p-5 space-y-4">
              {/* Local-only mode toggle */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-gray-900 dark:text-white font-medium">Local-Only Mode</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Store data only on this device (experimental)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleToggleLocalMode(!localOnlyMode);
                  }}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    localOnlyMode ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      localOnlyMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Clear data */}
              <button
                onClick={() => { setShowClearDataModal(true); setClearStep(1); setDeletePhrase(''); }}
                disabled={loading}
                className="w-full flex items-center justify-between px-4 py-3 bg-red-50 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50 border border-red-200"
              >
                <div className="flex items-center gap-3">
                  <Trash2 className="w-5 h-5 text-red-600" />
                  <div className="text-left">
                    <p className="text-red-900 font-medium">Clear All Data</p>
                    <p className="text-xs text-red-700">Permanently delete all your data</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-red-400" />
              </button>
            </div>
          </section>

          {/* Trash Section - Recently Deleted Items */}
          <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] dark:shadow-slate-900/20 overflow-hidden">
            <button
              onClick={() => setShowTrashSection(!showTrashSection)}
              className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-orange-600" />
                Trash
                {allDeletedItems.length > 0 && (
                  <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full">
                    {allDeletedItems.length}
                  </span>
                )}
              </h2>
              {showTrashSection ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            
            {showTrashSection && (
              <div className="p-5 border-t border-gray-200">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Deleted items are kept for 30 days before being permanently removed. 
                  View and restore items from all your shopping lists.
                </p>
                
                {loadingTrash ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                  </div>
                ) : allDeletedItems.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-xl border border-gray-200 border-dashed">
                    <Trash2 className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">No deleted items</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Items you delete will appear here
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {allDeletedItems.map((item) => {
                      // Calculate days until expiry (30-day retention)
                      const deletedDate = item.deletedAt instanceof Date
                        ? item.deletedAt
                        : new Date(item.deletedAt || Date.now());
                      const daysSinceDeleted = Math.floor((Date.now() - deletedDate.getTime()) / (1000 * 60 * 60 * 24));
                      const daysRemaining = Math.max(0, 30 - daysSinceDeleted);
                      const isExpiringSoon = daysRemaining <= 7;
                      
                      return (
                        <div
                          key={`${item.listId}-${item.id}`}
                          className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl p-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 dark:text-white truncate">{item.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                From: {item.listName}
                              </p>
                              <div className="flex items-center gap-2 flex-wrap mt-2">
                                <p className="text-xs text-gray-400">
                                  {formatDistanceToNow(deletedDate, { addSuffix: true })}
                                </p>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  isExpiringSoon 
                                    ? 'bg-red-100 text-red-700' 
                                    : 'bg-orange-100 text-orange-700'
                                }`}>
                                  {daysRemaining === 0 
                                    ? 'Expires today' 
                                    : daysRemaining === 1 
                                      ? 'Expires tomorrow' 
                                      : `${daysRemaining} days left`}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRestoreItem(item)}
                              disabled={restoringItemId === item.id}
                              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all flex-shrink-0 ${
                                restoringItemId === item.id
                                  ? 'bg-gray-100 text-gray-400'
                                  : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200'
                              }`}
                            >
                              <RotateCcw className="w-4 h-4" />
                              {restoringItemId === item.id ? 'Restoring…' : 'Restore'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {allDeletedItems.length > 0 && (
                  <button
                    onClick={loadAllDeletedItems}
                    disabled={loadingTrash}
                    className="mt-4 w-full px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 dark:bg-slate-600 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-500 transition-colors disabled:opacity-50"
                  >
                    {loadingTrash ? 'Refreshing...' : 'Refresh List'}
                  </button>
                )}
              </div>
            )}
          </section>

          {/* Notifications Section */}
          <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] dark:shadow-slate-900/20 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-yellow-600" />
                Notifications
              </h2>
            </div>
            <div className="p-5 space-y-4">
              {/* Budget alerts */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-gray-900 dark:text-white font-medium">Budget Alerts</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Get notified when approaching budget limits
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleToggleNotification('budget', !budgetAlerts);
                  }}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    budgetAlerts ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      budgetAlerts ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Goal reminders */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-gray-900 dark:text-white font-medium">Goal Reminders</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Reminders to contribute to your savings goals
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleToggleNotification('goal', !goalReminders);
                  }}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    goalReminders ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      goalReminders ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </section>

          {/* App Info Section */}
          <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] dark:shadow-slate-900/20 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Info className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                App Information
              </h2>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-slate-700 rounded-xl">
                <span className="text-gray-700 dark:text-gray-300">Version</span>
                <span className="text-gray-900 dark:text-white font-medium">1.0.0</span>
              </div>

              <button 
                onClick={() => navigate('/help')}
                className="w-full flex items-center justify-between px-4 py-3 bg-emerald-50 dark:bg-emerald-900/40 rounded-xl hover:bg-emerald-100 dark:hover:bg-emerald-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-emerald-600" />
                  <span className="text-emerald-900 font-medium">Help & User Guide</span>
                </div>
                <ChevronRight className="w-5 h-5 text-emerald-400" />
              </button>

              {/* Using native <a> tag for mailto: to avoid React SPA event blocking */}
              <a 
                href="mailto:support@getfinlist.com?subject=FinList Support Request"
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-slate-700 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors no-underline"
              >
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <span className="text-gray-900 dark:text-white">Contact Support</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </a>
            </div>
          </section>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-red-600 text-white rounded-2xl font-medium hover:bg-red-700 transition-colors shadow-lg"
          >
            <LogOut className="w-5 h-5" />
            Log Out
          </button>

          {/* Footer */}
          <div className="text-center py-4 space-y-2">
            <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              <a href="/terms" className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Terms</a>
              <span>·</span>
              <a href="/privacy" className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Privacy</a>
              <span>·</span>
              <a href="/cookies" className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Cookies</a>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Made with ❤️ by FinList Team
            </div>
          </div>
        </div>
      </div>

      <BottomNavigation />
      {/* Clear Data Modal */}
      {showClearDataModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !loading && setShowClearDataModal(false)} />
          <motion.div initial={{ scale:0.9, opacity:0 }} animate={{ scale:1, opacity:1 }} className="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-red-200 dark:border-red-700 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-red-100 bg-red-50">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-800">{clearStep === 1 ? 'Clear All Data' : 'Final Confirmation'}</h3>
              </div>
              <button disabled={loading} onClick={() => setShowClearDataModal(false)} className="p-1 rounded hover:bg-red-100">
                <X className="w-4 h-4 text-red-600" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {clearStep === 1 && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    You are about to permanently delete ALL of your financial data. This includes transactions, goals, budgets, shopping lists, categories, tasks & to-do lists and analytics meta. This cannot be undone.
                  </p>
                  <p className="text-sm text-red-700 font-medium">Are you absolutely sure you want to continue?</p>
                </div>
              )}
              {clearStep === 2 && (
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-800 space-y-1">
                    <p className="font-semibold">This will delete permanently:</p>
                    <ul className="list-disc ml-4 space-y-0.5">
                      <li>Transactions</li>
                      <li>Goals & Savings Goals</li>
                      <li>Tasks & To-Do Lists (FinTasks)</li>
                      <li>Shopping Lists</li>
                      <li>Budgets & Categories</li>
                      <li>Todo Meta / analytics summary</li>
                    </ul>
                    <p className="mt-2 font-medium">Type <span className="px-1 py-0.5 bg-white border border-red-300 rounded">{REQUIRED_PHRASE}</span> to confirm.</p>
                  </div>
                  <input
                    value={deletePhrase}
                    onChange={e => setDeletePhrase(e.target.value)}
                    placeholder={REQUIRED_PHRASE}
                    className="w-full px-3 py-2 border border-red-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400 text-sm"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">This action is <span className="text-red-600 font-semibold">IRREVERSIBLE</span>. Your data cannot be recovered after deletion.</p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                {clearStep === 1 && (
                  <>
                    <Button disabled={loading} variant="secondary" onClick={() => setShowClearDataModal(false)}>Cancel</Button>
                    <Button disabled={loading} variant="danger" onClick={() => setClearStep(2)}>Continue</Button>
                  </>
                )}
                {clearStep === 2 && (
                  <>
                    <Button disabled={loading} variant="secondary" onClick={() => { setClearStep(1); setDeletePhrase(''); }}>Back</Button>
                    <Button
                      disabled={loading || deletePhrase.trim().toUpperCase() !== REQUIRED_PHRASE}
                      variant="danger"
                      onClick={performClearAllData}
                    >
                      {loading ? 'Deleting…' : 'Delete Permanently'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
